import { useEffect } from 'react';
import type { Guide } from '../guides';
import LocalGuides from './LocalGuides';
import guideImages from '../guide-images.json';

// The editorial source files use paragraphs, H2s, lists, images and links.
// Render those as React nodes so source text never becomes executable HTML.
function inline(text: string) {
  return text
    .split(/(\*\*[^*]+\*\*|\[[^\]]+\]\((?:https:\/\/|\/(?![/\\]))[^\s)]+\))/g)
    .map((part, index) => {
      const link = part.match(
        /^\[([^\]]+)\]\(((?:https:\/\/|\/(?![/\\]))[^\s)]+)\)$/
      );
      if (link)
        return (
          <a
            key={index}
            href={link[2]}
            target={link[2].startsWith('https://') ? '_blank' : undefined}
            rel={
              link[2].startsWith('https://') ? 'noopener noreferrer' : undefined
            }
            className="text-forest underline underline-offset-4 hover:text-forest-hover"
          >
            {link[1]}
          </a>
        );
      return part.startsWith('**') ? (
        <strong key={index}>{part.slice(2, -2)}</strong>
      ) : (
        part
      );
    });
}

export default function GuidePage({ guide }: { guide?: Guide }) {
  useEffect(() => {
    const title = guide
      ? `${guide.title} | Turtle Island Cottage`
      : 'Guide not found | Turtle Island Cottage';
    document.title = title;
    const url = `https://turtleislandcottage.com${guide?.href ?? window.location.pathname}`;
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', url);
    const metadata = {
      'meta[name="description"]':
        guide?.description ??
        'Find local guides for your stay at Turtle Island Cottage.',
      'meta[property="og:title"]': title,
      'meta[name="twitter:title"]': title,
      'meta[property="og:description"]': guide?.description ?? '',
      'meta[name="twitter:description"]': guide?.description ?? '',
      'meta[property="og:url"]': url,
      'meta[property="og:type"]': guide ? 'article' : 'website'
    };
    Object.entries(metadata).forEach(([selector, content]) => {
      document.querySelector(selector)?.setAttribute('content', content);
    });
  }, [guide]);

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:block focus:p-4"
      >
        Skip to content
      </a>
      <header className="border-b border-forest/10 bg-birch">
        <nav
          aria-label="Guide navigation"
          className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-5"
        >
          <a
            href="/"
            className="font-display text-xl font-semibold italic text-forest"
          >
            Turtle Island Cottage
          </a>
          <a
            href="/guides/"
            className="text-sm font-semibold text-forest underline underline-offset-4"
          >
            All local guides
          </a>
        </nav>
      </header>
      <main id="main-content">
        <article className="mx-auto max-w-3xl px-5 py-12 sm:py-16">
          <p className="text-xs font-semibold uppercase tracking-widest text-bark/65">
            {guide?.category ?? 'Local guides'}
          </p>
          <h1 className="mt-5 font-display text-4xl font-semibold leading-tight text-forest sm:text-6xl">
            {guide?.title ?? 'This guide could not be found.'}
          </h1>
          {guide ? (
            <div className="guide-copy mt-8">
              <p className="text-xl text-forest">{guide.description}</p>
              {guide.blocks.map((block, index) => {
                const photos = block
                  .split('\n')
                  .map((line) =>
                    line.match(/^!\[([^\]]+)\]\((\/images\/[^\s)]+)\)$/)
                  );
                if (photos.every((photo) => photo))
                  return (
                    <div
                      key={index}
                      className={
                        photos.length > 1 ? 'grid gap-6 sm:grid-cols-2' : ''
                      }
                    >
                      {photos.map((photo) => {
                        const [, alt, src] = photo!;
                        const asset =
                          guideImages[src as keyof typeof guideImages];
                        return (
                          <figure key={src}>
                            <img
                              src={src}
                              srcSet={
                                asset
                                  ? `${asset.smallSrc} 800w, ${src} 1600w`
                                  : undefined
                              }
                              sizes={
                                photos.length > 1
                                  ? '(min-width: 768px) 352px, (min-width: 640px) calc((100vw - 64px) / 2), calc(100vw - 40px)'
                                  : '(min-width: 768px) 728px, calc(100vw - 40px)'
                              }
                              alt={alt}
                              width={asset?.width}
                              height={asset?.height}
                              loading={index === 0 ? 'eager' : 'lazy'}
                              decoding="async"
                              className="h-auto w-full rounded-xl"
                            />
                            <figcaption className="mt-2 text-sm leading-relaxed text-bark/70">
                              {alt}
                            </figcaption>
                          </figure>
                        );
                      })}
                    </div>
                  );
                if (block.startsWith('## '))
                  return <h2 key={index}>{inline(block.slice(3))}</h2>;
                if (block.startsWith('- '))
                  return (
                    <ul key={index}>
                      {block.split('\n').map((line, i) => (
                        <li key={i}>{inline(line.replace(/^- /, ''))}</li>
                      ))}
                    </ul>
                  );
                if (/^\d+\. /.test(block))
                  return (
                    <ol key={index}>
                      {block.split('\n').map((line, i) => (
                        <li key={i}>{inline(line.replace(/^\d+\. /, ''))}</li>
                      ))}
                    </ol>
                  );
                return <p key={index}>{inline(block)}</p>;
              })}
            </div>
          ) : (
            <p className="mt-6">
              Explore our local guides to plan your next lake day or weekend
              away.
            </p>
          )}
          <a
            href="/#contact"
            className="mt-10 inline-block rounded-xl bg-forest px-5 py-3 font-semibold text-white hover:bg-forest-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-4"
          >
            Ask about a stay
          </a>
        </article>
        <LocalGuides exclude={guide?.slug} />
      </main>
    </>
  );
}
